/*
 * Delugs — Reverse Strap Finder (strap PDP module), roadmap #30
 * ------------------------------------------------------------------
 * The normal Strap Finder fixes a WATCH and cycles STRAPS. This inverts it:
 * on a strap product page the STRAP is fixed and the customer swipes through
 * WATCHES — primarily the ones they own — each shown WEARING the strap.
 *
 * Rendering is the SHARED ENGINE (window.StrapFinderEngine.loadWatches, the
 * reverse mode added Jul 2026): same Flickity carousel, cell classes, crops
 * and surface CSS as every other finder surface. This file owns only the
 * strap-PDP concerns: which watches to show and in what order, per-watch
 * buckle matching, the footer bar, inline search, and instrumentation.
 *
 * Watch sources (newest engagement first, then brand-interleaved):
 *   1. recently viewed (localStorage 'sf_recent_watches')
 *   2. saved — anonymous (sessionStorage 'sf_session_watches', reversed)
 *   3. saved — logged in (customer metafield via Liquid, reversed = newest first)
 *   4. logged in with nothing saved → /api/strap-finder/customer-watches
 *      ("watches you've been looking at", from finder selections)
 *   5. no signal at all → the home page's featured brands, top compatible
 *      watch from each
 * Only watches whose lug width matches a width this strap is sold in are
 * shown — never render an impossible pairing.
 *
 * Instrumentation: strap_on_watch_viewed → /api/selections (sendBeacon,
 * same sf_sid as the drawer so sessions stitch).
 */
(function () {
  'use strict';

  var firstInt = function (s) {
    var m = String(s == null ? '' : s).match(/\d{1,2}/);
    return m ? parseInt(m[0], 10) : null;
  };
  var num = function (v) { var n = parseFloat(v); return isFinite(n) ? n : 0; };

  // Strip Liquid image_url params down to base + version — the engine applies
  // the region-crop transforms itself and a leftover &width= would fight them.
  function cleanUrl(url) {
    if (!url) return '';
    var base = url.split('?')[0];
    var v = '';
    (url.split('?')[1] || '').split('&').forEach(function (kv) { if (kv.indexOf('v=') === 0) v = kv.slice(2); });
    return v ? base + '?v=' + v : base;
  }

  // Buckle metal for a watch's case hardware, snapped to what this strap offers.
  function buckleForHardware(hw, available) {
    hw = String(hw == null ? '' : hw).toLowerCase();
    var want = 'silver';
    if (/yellow/.test(hw)) want = 'yellowgold';
    else if (/rose|pink|everose/.test(hw)) want = 'rosegold';
    else if (/black|dlc|pvd|ceramic|carbon|graphite/.test(hw)) want = 'black';
    else if (/gold/.test(hw)) want = 'yellowgold';
    // steel / silver / white gold / platinum / titanium / two-tone → silver
    if (available.indexOf(want) !== -1) return want;
    return available.indexOf('silver') !== -1 ? 'silver' : (available[0] || 'silver');
  }

  function ReverseStrapFinder(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.api = (cfg.apiBase || 'https://sf.delugs.com').replace(/\/$/, '');
    this.brandCache = {};
    this.indexProm = null;
    this.watches = [];
    this.engine = null;
    this._lastLogged = null;
    this._isFallback = false;
    this._source = null;
    this.booted = false;
    this.strapWidths = ((cfg.strap && cfg.strap.lugWidths) || [])
      .map(firstInt).filter(function (x) { return x != null; });
    var tops = (cfg.strap && cfg.strap.tops) || {};
    this.buckles = Object.keys(tops).filter(function (k) { return tops[k]; });
  }

  ReverseStrapFinder.prototype.boot = function () {
    if (this.booted) return;
    this.booted = true;
    var self = this;
    this.root.innerHTML = '<div class="rsf-shell"><div class="sfl-loading-overlay"><div class="sfl-spinner"></div></div></div>';
    this.collect()
      // collect() returns rows already ordered (own = newest-first brand-
      // interleaved BEFORE padding; inferred = recency; fallback = round-robin)
      .then(function (rows) { self.watches = rows; self.renderStage(); })
      .catch(function (e) {
        try { console.warn('[reverse-strap-finder]', e); } catch (e2) {}
        self.watches = [];
        self.renderStage();
      });
  };

  // ── Candidate gathering: newest engagement first ──
  ReverseStrapFinder.prototype.gatherCandidates = function () {
    var out = [], seen = {};
    var push = function (w, source) {
      if (!w || !w.brand || !w.reference) return;
      var key = (w.brand + '|' + w.reference).toLowerCase();
      if (seen[key]) return;
      seen[key] = true;
      out.push({ brand: w.brand, collection: w.collection || '', reference: w.reference, source: source });
    };
    // recently viewed is stored newest-first already
    try { JSON.parse(localStorage.getItem('sf_recent_watches') || '[]').forEach(function (w) { push(w, 'recent'); }); } catch (e) {}
    // saved lists are append-ordered → reverse for newest-first
    try {
      var sess = JSON.parse(sessionStorage.getItem('sf_session_watches') || '[]');
      sess.slice().reverse().forEach(function (w) { push(w, 'saved'); });
    } catch (e) {}
    (this.cfg.savedLoggedIn || []).slice().reverse().forEach(function (w) { push(w, 'saved'); });
    return out;
  };

  // ── Ordering: newest-first, brand-interleaved (Ken, Jul 2026) ──
  // Input is already newest-first; group by brand preserving that order, then
  // round-robin one per brand so no single brand monopolises the strip.
  ReverseStrapFinder.prototype.orderWatches = function (rows) {
    var groups = {}, brandOrder = [];
    rows.forEach(function (w) {
      var b = (w.brand || '?').toLowerCase();
      if (!groups[b]) { groups[b] = []; brandOrder.push(b); }
      groups[b].push(w);
    });
    var out = [], added = true;
    while (added) {
      added = false;
      for (var i = 0; i < brandOrder.length; i++) {
        var g = groups[brandOrder[i]];
        if (g.length) { out.push(g.shift()); added = true; }
      }
    }
    return out;
  };

  // ── Split-API helpers ──
  ReverseStrapFinder.prototype.loadIndex = function () {
    if (this.indexProm) return this.indexProm;
    this.indexProm = fetch(this.api + '/api/strap-finder/index')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var map = {};
        (d.brands || []).forEach(function (b) { map[b.name.toLowerCase()] = b; });
        return map;
      });
    return this.indexProm;
  };

  ReverseStrapFinder.prototype.loadBrand = function (brandEntry) {
    var self = this;
    var key = brandEntry.name.toLowerCase();
    if (this.brandCache[key]) return Promise.resolve(this.brandCache[key]);
    var url = this.api + '/api/strap-finder/brand/' + encodeURIComponent(brandEntry.name) +
              '?v=' + encodeURIComponent(brandEntry.v || '');
    return fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (d) { var rows = (d && d.sheet1) || []; self.brandCache[key] = rows; return rows; })
      .catch(function () { self.brandCache[key] = []; return []; });
  };

  ReverseStrapFinder.prototype.isCompatible = function (row) {
    if (!row || !row.watchHeadImage) return false;
    var lw = firstInt(row.lugWidth);
    if (lw == null) return false;                 // blank / proprietary → never guess
    if (!this.strapWidths.length) return true;
    return this.strapWidths.indexOf(lw) !== -1;
  };

  ReverseStrapFinder.prototype.findRow = function (rows, brand, reference) {
    var refClean = String(reference || '').trim().toLowerCase();
    var exact = null, pref = null;
    for (var i = 0; i < rows.length; i++) {
      var rr = String(rows[i].reference || '').trim().toLowerCase();
      if (!rr) continue;
      if (rr === refClean) { exact = rows[i]; break; }
      if (!pref && (rr.indexOf(refClean) === 0 || refClean.indexOf(rr) === 0) && refClean.length >= 4) pref = rows[i];
    }
    return exact || pref;
  };

  ReverseStrapFinder.prototype.collect = function () {
    var self = this;
    var candidates = this.gatherCandidates();
    return this.loadIndex().then(function (index) {
      var brandsNeeded = {};
      candidates.forEach(function (c) { var be = index[c.brand.toLowerCase()]; if (be) brandsNeeded[be.name.toLowerCase()] = be; });
      var brandList = Object.keys(brandsNeeded).map(function (k) { return brandsNeeded[k]; });
      return Promise.all(brandList.map(function (be) { return self.loadBrand(be); })).then(function () {
        var own = [];
        candidates.forEach(function (c) {
          var be = index[c.brand.toLowerCase()];
          if (!be) return;
          var row = self.findRow(self.brandCache[be.name.toLowerCase()] || [], c.brand, c.reference);
          if (row && self.isCompatible(row) && own.indexOf(row) === -1) own.push(row);
        });
        if (own.length) { self._isFallback = false; self._source = 'own'; return self.padSmallList(self.orderWatches(own), index); }
        if (self.cfg.customerId) {
          return self.collectInferred().then(function (inf) {
            if (inf.length) { self._isFallback = false; self._source = 'inferred'; return self.padSmallList(inf, index); }
            self._isFallback = true; self._source = 'fallback';
            return self.collectFallback(index);
          });
        }
        self._isFallback = true; self._source = 'fallback';
        return self.collectFallback(index);
      });
    });
  };

  // A 1-4 watch strip looks broken (it used to wraparound-clone the same
  // watch across the whole carousel — Ken, Jul 3). Pad small personal lists
  // to 8 with popular watches that fit this strap; the customer's own
  // watches always lead. Ordering happens BEFORE padding so the round-robin
  // never mixes popular picks into their collection.
  ReverseStrapFinder.prototype.padSmallList = function (list, index) {
    if (list.length >= 6) return Promise.resolve(list);
    var self = this;
    return this.collectFallback(index).then(function (fb) {
      var seen = {};
      list.forEach(function (w) { seen[((w.brand || '') + '|' + (w.reference || '')).toLowerCase()] = 1; });
      var pad = fb.filter(function (w) { return !seen[((w.brand || '') + '|' + (w.reference || '')).toLowerCase()]; });
      return list.concat(pad.slice(0, Math.max(0, 8 - list.length)));
    }).catch(function () { return list; });
  };

  // Case 2: logged in, nothing saved — finder-engagement history.
  ReverseStrapFinder.prototype.collectInferred = function () {
    var self = this;
    return fetch(self.api + '/api/strap-finder/customer-watches?customerId=' + encodeURIComponent(self.cfg.customerId))
      .then(function (r) { return r.ok ? r.json() : { watches: [] }; })
      .then(function (d) { return (d.watches || []).filter(function (w) { return self.isCompatible(w); }); })
      .catch(function () { return []; });
  };

  // No personal signal: mirror the home page's "Find Straps for Your Watch"
  // brand cards — top compatible watch from each featured brand.
  ReverseStrapFinder.prototype.collectFallback = function (index) {
    var self = this;
    var brands = (this.cfg.fallbackBrands || ['Rolex', 'Omega', 'Tudor', 'Cartier', 'IWC', 'Patek Philippe', 'Grand Seiko', 'Breitling'])
      .map(function (n) { return index[n.toLowerCase()]; }).filter(Boolean);
    return Promise.all(brands.map(function (be) { return self.loadBrand(be); })).then(function (lists) {
      var picks = [], idx = 0, added = true;
      while (picks.length < 12 && added) {
        added = false;
        for (var b = 0; b < lists.length; b++) {
          var rows = lists[b] || [], found = 0;
          for (var i = 0; i < rows.length; i++) {
            if (!self.isCompatible(rows[i])) continue;
            if (found === idx) { if (picks.indexOf(rows[i]) === -1) { picks.push(rows[i]); added = true; } break; }
            found++;
          }
          if (picks.length >= 12) break;
        }
        idx++;
      }
      return picks;
    });
  };

  // ── Map watch rows → engine items (per-watch buckle + margins) ──
  ReverseStrapFinder.prototype.buildItems = function () {
    var self = this, s = this.cfg.strap;
    var integrated = !!s.isIntegrated;
    return this.watches.map(function (w) {
      var bk = buckleForHardware(w.caseHardwareColour, self.buckles);
      var top = (s.tops && (s.tops[bk] || s.tops.silver)) || '';
      var bottom = (s.bottoms && (s.bottoms[bk] || s.bottoms.silver)) || '';
      if (!top) { for (var k in s.tops) { if (s.tops[k]) { top = s.tops[k]; break; } } }
      if (!bottom) { for (var k2 in s.bottoms) { if (s.bottoms[k2]) { bottom = s.bottoms[k2]; break; } } }
      return {
        head: cleanUrl(w.watchHeadImage),
        top: cleanUrl(top),
        bottom: cleanUrl(bottom),
        topMargin: num(integrated ? w.sfIntegratedTopMargin : w.sfTopMargin),
        bottomMargin: num(integrated ? w.sfIntegratedBottomMargin : w.sfBottomMargin),
        title: [w.brand, w.model || w.collection].filter(Boolean).join(' '),
        handle: w.reference || '',
        alt: [w.brand, w.model || w.collection].filter(Boolean).join(' ') + ' on ' + s.title,
      };
    });
  };

  ReverseStrapFinder.prototype.findByRef = function (ref) {
    ref = String(ref || '').toLowerCase();
    for (var i = 0; i < this.watches.length; i++) {
      if (String(this.watches[i].reference || '').toLowerCase() === ref) return this.watches[i];
    }
    return null;
  };

  // ── Stage ──
  ReverseStrapFinder.prototype.renderStage = function () {
    var self = this;
    var empty = this.watches.length === 0;

    // Proper section header (PDP eyebrow + heading language). The subline is
    // conversion copy for visitors WITHOUT their own watches only; when we're
    // already showing their watches it says nothing (Ken, Jul 2 — the "N of
    // your watches fit" line removed).
    var sub = empty ? 'Search for your watch to see this strap on it.'
      : this._source === 'inferred' ? "Watches you've been looking at — each wearing this strap."
      : this._isFallback ? 'Popular watches this strap fits — add yours to see it on your own.'
      : '';
    var head =
      '<div class="rsf-header">' +
        '<span class="rsf-header__eyebrow">Strap Finder</span>' +
        '<h2 class="rsf-header__title">See it on your watch</h2>' +
        (sub ? '<p class="rsf-header__sub">' + sub + '</p>' : '') +
      '</div>';

    // NOTE: deliberately NOT the model page's `products carousel row ...`
    // grid classes — on PRODUCT templates the theme's own product-grid
    // carousel manager targets `.products.carousel` and clamps the Flickity
    // viewport to its (absent) .product-card height (~8px). The surface CSS
    // only needs .strap-finder__straps-container (+ .strap-count-more, which
    // the engine adds).
    var stage =
      '<div class="rsf-stage"' + (empty ? ' style="display:none"' : '') + '>' +
        '<div class="strap-finder__straps-container rsf-reverse sf-front-end"></div>' +
      '</div>';

    // Compact fixed-geometry info card — TWO rows on desktop:
    //   row 1: watch name · fit · count (one line, ellipsed name)
    //   row 2: all three actions (solid CTA / outline link / quiet search toggle)
    // Nothing jumps when the watch changes, and it stays short.
    var bar =
      '<div class="rsf-card"' + (empty ? ' style="display:none"' : '') + '>' +
        '<div class="rsf-card__line">' +
          '<b class="rsf-card__name"></b>' +
          '<span class="rsf-card__fit"></span>' +
          '<span class="rsf-card__count"></span>' +
          '<button type="button" class="rsf-card__save">&#9825; Save</button>' +
        '</div>' +
        '<div class="rsf-card__actions">' +
          '<button type="button" class="rsf-card__cta">Shop this strap</button>' +
          '<a class="rsf-card__ghost" href="#" target="_self">Other straps for this watch →</a>' +
          '<button type="button" class="rsf-search__toggle">＋ Try another watch</button>' +
        '</div>' +
      '</div>';

    // Search: open by default when the visitor has NO watches of their own
    // (empty state AND popular-fallback state) — "which watch do you own?" is
    // the conversion moment. Picked watches persist to recently-viewed, so
    // they stay "their watches" on every strap PDP. The full finder link goes
    // to where saving/collections live.
    var searchOpen = empty || this._isFallback;
    var search =
      '<div class="rsf-search' + (searchOpen ? ' is-open' : '') + (empty ? ' rsf-search--solo' : '') + '">' +
        (empty ? '<p class="rsf-search__lead">Which watch do you own? Search to see this strap on it.</p>' : '') +
        '<div class="rsf-search__panel"' + (searchOpen ? '' : ' hidden') + '>' +
          '<input type="text" class="rsf-search__input" autocomplete="off" inputmode="search" ' +
            'placeholder="Which watch do you own? e.g. Tudor BB58, Speedmaster..."' + '>' +
          '<div class="rsf-search__results"></div>' +
          '<a class="rsf-search__finder-link" href="/pages/strap-finder">Browse all watches in the Strap Finder →</a>' +
        '</div>' +
      '</div>';

    this.root.innerHTML = '<div class="rsf-shell">' + head + stage + bar + search + '</div>';
    this.wireSearch();
    if (empty) return;

    this.root.querySelector('.rsf-card__cta').addEventListener('click', function () { self.shopThisStrap(); });
    this.root.querySelector('.rsf-card__save').addEventListener('click', function () { if (self._current) self.saveToCollection(self._current); });
    this.startEngine();
  };

  // ── Shared engine (reverse mode) ──
  ReverseStrapFinder.prototype.startEngine = function () {
    var self = this;
    var tries = 0;
    (function waitDeps() {
      if (typeof window.StrapFinderEngine === 'function' && window.jQuery && window.jQuery.fn.flickity && window.imagesLoaded) {
        self.engine = window.StrapFinderEngine({
          $: window.jQuery,
          container: self.root.querySelector('.rsf-reverse'),
          overlay: null,
          onSelect: function (sel) { self.onWatchSelected(sel); },
          onCounter: function (current, total) {
            var el = self.root.querySelector('.rsf-card__count');
            if (el) el.textContent = total ? current + ' / ' + total : '';
          },
        });
        self.engine.loadWatches(self.buildItems());
      } else if (tries++ < 100) {
        setTimeout(waitDeps, 100);
      }
    })();
  };

  ReverseStrapFinder.prototype.onWatchSelected = function (sel) {
    var self = this;
    var w = this.findByRef(sel.handle);
    if (!w) return;
    this._current = w;
    this.updateSaveButton(w);
    var nameEl = this.root.querySelector('.rsf-card__name');
    var fitEl = this.root.querySelector('.rsf-card__fit');
    var altEl = this.root.querySelector('.rsf-card__ghost');
    if (nameEl) nameEl.textContent = sel.title;
    if (fitEl) fitEl.textContent = (firstInt(w.lugWidth) ? '· ' + firstInt(w.lugWidth) + 'mm — fits' : '');
    if (altEl) {
      var lp = w.modelLandingPage || '';
      // collapse fully when missing — a visibility-hidden ghost leaves an odd
      // gap in the actions row (Ken, Jul 2). With the search endpoint's
      // landing-page fallback this is rare anyway.
      if (lp) { altEl.style.display = ''; altEl.setAttribute('href', lp); } else altEl.style.display = 'none';
    }
    this._logTimer && clearTimeout(this._logTimer);
    this._logTimer = setTimeout(function () { self.logView(w); }, 500);
  };

  // ── Inline search (live /search endpoint) ──
  ReverseStrapFinder.prototype.wireSearch = function () {
    var self = this;
    var wrap = this.root.querySelector('.rsf-search');
    if (!wrap) return;
    // The toggle lives in the CARD's actions row (not inside .rsf-search) —
    // query from the module root, or the listener never attaches.
    var toggle = this.root.querySelector('.rsf-search__toggle');
    var panel = wrap.querySelector('.rsf-search__panel');
    var input = wrap.querySelector('.rsf-search__input');
    var results = wrap.querySelector('.rsf-search__results');
    if (toggle) toggle.addEventListener('click', function () {
      var open = wrap.classList.toggle('is-open');
      panel.hidden = !open;
      if (open) input.focus();
    });
    var run = debounce(function () {
      var q = input.value.trim();
      if (q.length < 2) { results.innerHTML = ''; return; }
      results.innerHTML = '<div class="rsf-search__hint">Searching…</div>';
      fetch(self.api + '/api/strap-finder/search?q=' + encodeURIComponent(q))
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var rows = (d.results || []).filter(function (w) { return self.isCompatible(w); }).slice(0, 8);
          if (!rows.length) {
            results.innerHTML = '<div class="rsf-search__hint">No matching watch fits this strap (' + self.strapWidths.join('/') + 'mm).</div>';
            return;
          }
          results.innerHTML = rows.map(function (w, i) {
            var thumb = w.watchHeadImage ? cleanUrl(w.watchHeadImage) + '?width=80' : '';
            return '<button type="button" class="rsf-search__item" data-i="' + i + '">' +
              (thumb ? '<img src="' + thumb + '" alt="" loading="lazy">' : '<span class="rsf-search__noimg"></span>') +
              '<span class="rsf-search__meta"><b>' + esc(w.brand + ' ' + (w.model || w.collection || '')) + '</b>' +
              '<span>' + esc(w.reference || '') + ' · ' + (firstInt(w.lugWidth) || '?') + 'mm</span></span></button>';
          }).join('');
          var items = results.querySelectorAll('.rsf-search__item');
          for (var k = 0; k < items.length; k++) {
            (function (idx) {
              items[idx].addEventListener('click', function () {
                self.addWatch(rows[idx]);
                input.value = ''; results.innerHTML = '';
                if (toggle) { wrap.classList.remove('is-open'); panel.hidden = true; }
              });
            })(k);
          }
        })
        .catch(function () { results.innerHTML = '<div class="rsf-search__hint">Search unavailable.</div>'; });
    }, 250);
    if (input) {
      input.addEventListener('input', run);
      input.addEventListener('keydown', function (e) { if (e.key === 'Enter') e.preventDefault(); });
    }
  };

  // Add a searched watch: front of the strip (engine re-renders, Flickity
  // starts at cell 0 = the new watch), persisted to recently-viewed.
  ReverseStrapFinder.prototype.addWatch = function (w) {
    if (!w || !this.isCompatible(w)) return;
    var key = (w.brand + '|' + w.reference).toLowerCase();
    this.watches = this.watches.filter(function (x) {
      return (x.brand + '|' + x.reference).toLowerCase() !== key;
    });
    this.watches.unshift(w);
    this._isFallback = false;
    this.saveRecent(w);
    this._lastLogged = null;
    if (!this.engine) { this.renderStage(); return; }
    // once they've added their own watch the conversion subline no longer applies
    var sub = this.root.querySelector('.rsf-header__sub');
    if (sub) sub.remove();
    var stage = this.root.querySelector('.rsf-stage'); if (stage) stage.style.display = '';
    var card = this.root.querySelector('.rsf-card'); if (card) card.style.display = '';
    this.engine.loadWatches(this.buildItems());
  };

  ReverseStrapFinder.prototype.saveRecent = function (w) {
    try {
      var recent = JSON.parse(localStorage.getItem('sf_recent_watches') || '[]');
      recent = recent.filter(function (r) { return r.reference !== w.reference; });
      recent.unshift({ brand: w.brand, collection: w.collection || '', reference: w.reference, image: w.watchImage || '', ts: (window.Date ? Date.now() : 0) });
      localStorage.setItem('sf_recent_watches', JSON.stringify(recent.slice(0, 8)));
    } catch (e) {}
  };

  // Keep the buyer on THIS strap: pre-select the lug-width variant matching
  // the centred watch (the PDP's width <select> lives in
  // fieldset.lug-width-select, values like "20-16" = lug-buckle), then scroll
  // to the buy box. Dispatching 'change' runs the theme's own handlers
  // (price, buckle option, OOS state).
  ReverseStrapFinder.prototype.shopThisStrap = function () {
    var w = this._current || null;
    if (w) this.logEvent('strap_clicked', w);   // view→intent funnel
    var lug = w ? firstInt(w.lugWidth) : null;
    var fs = document.querySelector('fieldset.lug-width-select');
    var sel = fs ? fs.querySelector('select') : null;
    if (sel && lug != null) {
      var opt = null;
      for (var i = 0; i < sel.options.length; i++) {
        var v = (sel.options[i].value || '').trim();
        if (!v) continue;
        if (firstInt(v.split('-')[0]) === lug) { opt = sel.options[i]; break; }
      }
      if (opt && sel.value !== opt.value) {
        sel.value = opt.value;
        try { sel.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
      }
    }
    var target = (fs && fs.offsetParent) ? fs :
      (document.querySelector('form[action*="/cart/add"]') || document.querySelector('product-form') || document.querySelector('.product-form') || document.querySelector('main') || document.body);
    try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e2) { window.scrollTo(0, 0); }
  };

  // ── Save to collection (♡) — reuses the DRAWER's own globals, so the flow
  // (customer metafield via Mechanic webhook + CRM tags, or anonymous
  // sessionStorage that migrates on login) is identical to saving from the
  // finder. Falls back to a direct sessionStorage write if the drawer JS
  // isn't on the page.
  ReverseStrapFinder.prototype.isSaved = function (w) {
    if (!w) return false;
    var key = ((w.brand || '') + '|' + (w.reference || '')).toLowerCase();
    if (this._savedNow && this._savedNow[key]) return true;
    var match = function (x) { return ((x.brand || '') + '|' + (x.reference || '')).toLowerCase() === key; };
    if ((this.cfg.savedLoggedIn || []).some(match)) return true;
    try { if (JSON.parse(sessionStorage.getItem('sf_session_watches') || '[]').some(match)) return true; } catch (e) {}
    return false;
  };

  ReverseStrapFinder.prototype.saveToCollection = function (w) {
    if (!w || this.isSaved(w)) return;
    var watchData = {
      brand: w.brand || '', model: w.model || '', collection: w.collection || '',
      reference: w.reference || '', identifier: w.identifier || '',
      watchImage: w.watchImage || '', url: w.modelLandingPage || ''
    };
    try {
      if (this.cfg.customerId && typeof window.addToWatchList === 'function') window.addToWatchList(watchData);
      else if (typeof window.addToSessionStorage === 'function') window.addToSessionStorage(watchData);
      else {
        var list = JSON.parse(sessionStorage.getItem('sf_session_watches') || '[]');
        list.push(watchData);
        sessionStorage.setItem('sf_session_watches', JSON.stringify(list.slice(0, 50)));
      }
    } catch (e) { return; }
    this._savedNow = this._savedNow || {};
    this._savedNow[((w.brand || '') + '|' + (w.reference || '')).toLowerCase()] = true;
    this.updateSaveButton(w);
    this.logEvent('watch_saved', w);   // deliberate save = strongest signal
  };

  ReverseStrapFinder.prototype.updateSaveButton = function (w) {
    var btn = this.root.querySelector('.rsf-card__save');
    if (!btn) return;
    var saved = this.isSaved(w);
    btn.classList.toggle('is-saved', saved);
    btn.innerHTML = saved ? '&#10003; Saved' : '&#9825; Save';
    btn.disabled = saved;
  };

  // ── Instrumentation ──
  // strap_on_watch_viewed  — a watch settled centre-stage wearing the strap
  // strap_clicked          — "Shop this strap" tapped (view→intent funnel;
  //                          referrer /products/... distinguishes it from the
  //                          forward finder's strap clicks on /pages/...)
  // watch_saved            — ♡ saved a watch to their collection from the PDP
  ReverseStrapFinder.prototype.logEvent = function (eventType, w) {
    if (!w) return;
    var sid = '';
    try { sid = localStorage.getItem('sf_sid') || sessionStorage.getItem('sf_sid') || ''; } catch (e) {}
    if (!sid) { sid = 's' + Math.random().toString(36).slice(2) + (window.Date ? Date.now().toString(36) : ''); try { localStorage.setItem('sf_sid', sid); } catch (e2) {} }
    var payload = JSON.stringify({
      eventType: eventType,
      strap: this.cfg.strap.title || this.cfg.strap.handle || '',
      brand: w.brand || '', collection: w.collection || '', reference: w.reference || '',
      // Liquid emits customer.id as a NUMBER; the API's clip() only accepts
      // strings, so an unstringified id silently drops the attribution.
      customerId: this.cfg.customerId ? String(this.cfg.customerId) : '', sessionId: sid,
      pageType: this._isFallback ? 'strap_pdp_fallback' : 'strap_pdp',
      referrer: window.location.pathname
    });
    var url = this.api + '/api/selections';
    try {
      if (navigator.sendBeacon) navigator.sendBeacon(url, new Blob([payload], { type: 'text/plain;charset=UTF-8' }));
      else fetch(url, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body: payload, keepalive: true }).catch(function () {});
    } catch (e3) {}
  };

  ReverseStrapFinder.prototype.logView = function (w) {
    if (!w) return;
    var key = (w.brand + '|' + w.reference).toLowerCase();
    if (this._lastLogged === key) return;
    this._lastLogged = key;
    this.logEvent('strap_on_watch_viewed', w);
  };

  // ── utils ──
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
  function debounce(fn, ms) { var t; return function () { var a = arguments, c = this; clearTimeout(t); t = setTimeout(function () { fn.apply(c, a); }, ms); }; }

  // NOTE: an above-the-fold entry chip (injected beside the lug-width
  // selector) was tried Jul 3 and REMOVED same day — Ken rejected the
  // buy-box placement. Don't re-add there; if discovery needs a boost after
  // the data readout, design a different placement with Ken first.

  // ── Boot (lazy — only when scrolled near, so the PDP isn't slowed) ──
  function init() {
    var root = document.getElementById('reverse-strap-finder');
    if (!root || root.__rsfBooted) return;
    var cfgEl = document.getElementById('rsf-config');
    if (!cfgEl) return;
    var cfg;
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    if (!cfg || !cfg.strap) return;
    root.__rsfBooted = true;
    var rsf = new ReverseStrapFinder(root, cfg);
    window.ReverseStrapFinderInstance = rsf;
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) { io.disconnect(); rsf.boot(); } });
      }, { rootMargin: '300px 0px' });
      io.observe(root);
    } else { rsf.boot(); }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
